import { evaluatePotentCantrip } from './wizard/evokerAttacks';
import { createHitFeatureEvaluator, getClassFeatureHooks } from './registry';
import { evaluateWeaponMasteryMissFeatures } from '../utils/weaponMasteryGraze';

const normalizedId = (value) => value == null ? null : String(value).trim() || null;

const idOf = (participant) => normalizedId(participant?.characterId || participant?.enemyId ||
  participant?.combatantId || participant?.id || participant?._id);

const entityTypeOf = (entity) => {
  const type = String(entity?.entityType || '').toLowerCase();
  if (type === 'character') return 'character';
  if (type === 'enemy' || type === 'monster') return 'monster';
  return 'unknown';
};

/** Join registry-owned discovery to metadata already normalized by the player producer. */
export const createPlayerAttackFeatureIntegration = ({
  character,
  attackerId,
  targetId,
  target,
  attack,
  ability,
  abilityModifier,
  proficiencyBonus,
  combatState,
  isWeaponAttack,
  isMelee,
  isRanged,
  isFinesse,
  getHooks = getClassFeatureHooks,
  queries,
}) => {
  // Hooks read active effects (e.g. Hunter's Mark) through queries; callers may add more queries.
  const hookQueries = { activeEffects: Array.isArray(combatState?.activeEffects) ? combatState.activeEffects : [], ...(queries || {}) };
  const evaluateHitFeatures = createHitFeatureEvaluator(
    getHooks(character, 'evaluateOnHit'),
    hookQueries,
  );
  const participants = Array.isArray(combatState?.participants) ? combatState.participants : [];
  const participantIds = participants.map(idOf).filter(Boolean);
  const normalizedAttackerId = normalizedId(attackerId);
  const normalizedTargetId = normalizedId(targetId);
  return {
    ...(evaluateHitFeatures ? { evaluateHitFeatures } : {}),
    evaluateMissFeatures: async (miss) => [...evaluateWeaponMasteryMissFeatures(miss), ...await evaluatePotentCantrip({ ...miss, character, rollDice: queries?.rollDice })],
    hitFeatureContext: {
      attacker: {
        entityType: 'character',
        entity: character,
        participant: participants.find((participant) => idOf(participant) === normalizedAttackerId) || null,
      },
      target: {
        entityType: entityTypeOf(target),
        entity: target,
        participant: participants.find((participant) => idOf(participant) === normalizedTargetId) || null,
      },
      attack: {
        name: attack.name,
        kind: attack.kind === 'cantrip' ? 'spell' : attack.kind,
        source: attack.weapon || attack.spell || null,
        ability: ability || null,
        abilityModifier,
        proficiencyBonus,
        damageType: attack.damageType || null,
        isWeaponAttack,
        isMelee,
        isRanged,
        isFinesse,
      },
      combat: {
        campaignId: combatState?.campaignId || null,
        round: Number.isInteger(combatState?.round) ? combatState.round : null,
        turnSequence: Number.isInteger(combatState?.turnSequence) ? combatState.turnSequence : null,
        activeCombatantId: idOf(participants[combatState?.activeTurn]) || null,
        participantIds,
        ...(combatState?.turnFeatureUsage ? { turnFeatureUsage: combatState.turnFeatureUsage } : {}),
      },
      combatState,
    },
  };
};

// Retain the original public name for callers outside the player attack UI.
export const createPlayerHitFeatureIntegration = createPlayerAttackFeatureIntegration;
